<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="data-subjects-page-title" class="mb-2">
          {{ $t('maintenance.data_subjects.title') }}
        </h4>
        <div>
          <span v-if="getGuide('data_subjects_list')">
            <GuideButton :guide="getGuide('data_subjects_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="filter-ds-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <router-link
            to="/maintenance/data-subjects/create"
            class="btn btn-success float-right search-bar"
            qid="create-ds-button"
            v-if="isGranted(permissions.DATA_SUBJECT_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.data_subjects.create_button') }}
        </router-link>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3"
          qid="search-ds-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.data_subjects.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sort-ds-name"
              >
                {{ $t('maintenance.data_subjects.table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('number_of_subjects')"
                  qid="ds-sort-subjects"
              >
                {{ $t('maintenance.data_subjects.table.number_of_subjects') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'number_of_subjects'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'number_of_subjects'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'number_of_subjects'"/>
                </span>
              </th>
              <th width="130"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'maintenance.data_subjects.view', params: { slug: object.slug }}"
                      qid="ds-view-page-link"
                  >
                    {{object.name}}
                  </router-link>
                </td>
                <td>
                  <span qid="ds-number-of-subjects">
                    {{ object.number_of_subjects }}
                  </span>
                </td>
                <td>
                  <span v-if="isGranted([permissions.DATA_SUBJECT_EDIT, permissions.DATA_SUBJECT_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-ds-modal"
                        @click="showEditDSModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.DATA_SUBJECT_DELETE, permissions.DATA_SUBJECT_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-ds-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="filter-ds-subjects-label">
                {{ $t('maintenance.data_subjects.create.subjects_label') }}
              </label>
              <multiselect
                  v-model="filters.number_of_subjects"
                  :options="nosItems"
                  :placeholder="$t('maintenance.data_subjects.create.subjects_placeholder')"
                  label="number_of_subjects"
                  @select="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  @remove="debounceInput"
                  qid="filter-ds-form-subjects"
              ></multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="ds-filters-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete data subject confirm modal template -->
    <b-modal ref="delete-data-subjects" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ds-title">
          {{ $t('maintenance.data_subjects.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.data_subjects.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.data_subjects.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-ds-cancel-button"
        >
          {{ $t('maintenance.data_subjects.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteDS(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ds-submit-button"
                  v-if="isGranted([permissions.DATA_SUBJECT_DELETE, permissions.DATA_SUBJECT_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.data_subjects.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit data subject modal template -->
    <b-modal ref="edit-data-subjects" hide-footer @hide="hideEditDSModal">
      <div class="d-block text-center">
        <h4 qid="update-ds-title">
          {{ $t('maintenance.data_subjects.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ds-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editDSFormSubmit" qid="create-ds-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-ds-name-label">
              {{ $t('maintenance.data_subjects.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-ds-form-name"
              type="text"
              v-model="dsForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.data_subjects.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-ds-description-label">
              {{ $t('maintenance.data_subjects.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-ds-form-description"
              v-model="dsForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.data_subjects.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group :class="{'multiselect--show-input': nosInput.length}">
            <label qid="edit-ds-subjects-label">
              {{ $t('maintenance.data_subjects.create.subjects_label') }}
            </label>
            <multiselect
                v-model="dsForm.number_of_subjects"
                :options="nosItems"
                :placeholder="$t('maintenance.data_subjects.create.subjects_placeholder')"
                :preserveSearch="true"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="handleNosInput"
                track-by="number_of_subjects"
                label="number_of_subjects"
                qid="edit-ds-form-subjects"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.data_subjects.create.subjects_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditDSModal"
                    qid="edit-ds-cancel-button"
          >
            {{ $t('maintenance.data_subjects.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="edit-ds-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.data_subjects.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'DataSubjects',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.data_subjects.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        dsForm: {
          name: '',
          description: '',
          number_of_subjects: [],
          client:'',
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          number_of_subjects: [],
          keywordValue: ''
        },
        nosItems: [],
        nosInput: '',
        loading: true,
        buttonDisabled: false,
        myGuides: [],
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
        this.loadSubjects();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      resetFilters() {
        this.filters.number_of_subjects = ''
        this.debounceInput()
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      handleNosInput(input) {
        this.nosInput = input;
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.number_of_subjects && this.filters.number_of_subjects.number_of_subjects)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('data-subjects', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            number_of_subjects: this.filters.number_of_subjects ? this.filters.number_of_subjects.number_of_subjects : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-data-subjects'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-data-subjects'].hide()
      },
      showEditDSModal(object) {
        this.error = '';
        let self = this;

        this.dsForm.number_of_subjects = []
        // Preselect number of subjects on edit modal
        if (object.number_of_subjects) {
          _.find(this.nosItems, function(item) {
            if (item.number_of_subjects == object.number_of_subjects) {
              self.dsForm.number_of_subjects = item;
            }
          });
        }

        this.dsForm.slug = object.slug;
        this.dsForm.name = object.name;
        this.dsForm.description = object.description;
        this.$refs['edit-data-subjects'].show()
      },
      hideEditDSModal() {
        this.loadSubjects();
        this.nosInput = ''
        this.$refs['edit-data-subjects'].hide()
      },
      editDSFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.dsForm.client = this.getClient.slug;

        if (this.nosInput && this.nosInput.length) {
          this.dsForm.number_of_subjects = this.nosInput
        } else {
          if (this.dsForm.number_of_subjects && this.dsForm.number_of_subjects.number_of_subjects) {
            this.dsForm.number_of_subjects = this.dsForm.number_of_subjects.number_of_subjects
          } else {
            this.dsForm.number_of_subjects = '';
          }
        }

        piincHttp.put('data-subjects/' + this.dsForm.slug, this.dsForm).then(function() {
          self.hideEditDSModal();
          self.loadContent(self.currentPage);
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.data_subjects.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.number_of_subjects) {
              self.error += ' ' + error.data.errors.number_of_subjects[0]
            }
          }
        })
      },
      confirmDeleteDS(object) {
        let self = this;
        piincHttp.delete('data-subjects/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.data_subjects.delete.success'))
        }, function() {});
      },
      loadSubjects() {
        let self = this;
        if (this.getClient && this.getClient.slug) {
          piincHttp.get('/data-subjects/subjects', { params:
            {
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.nosItems = response.data
            if (self.nosItems && !self.nosItems.length) {
              self.nosItems.push({number_of_subjects: self.$t('system.no_results'), $isDisabled: true})
            }
          }, function() {});
        }
      }
    },
    components: {
      GuideButton
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'data_subjects',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.data_subjects) {
          this.filters = this.getFilters.data_subjects
        }
        this.loadContent();
      }
      this.loadSubjects();
      this.loadGuides('data_subjects_list');
    }
  }
</script>
